import { buildLoLpFullName } from "./common-helpers";

// Step 1 contact data for updateLoan / updateFields — the same payload the Add New Loan
// wizard builds in edit-loan-application/index.jsx handleNext (tpo: 1139-1239,
// originatorInfoData: 1243-1286, org fields: updateField 739-776) and the AE email
// fields create-loan-application/index.jsx writes after createLoan (418-430).
// The Dashboard has no manual-originator mode, so only the broker-list branches apply.

const pickOrganization = (org) => ({
	name: org?.name,
	id: org?.company_id,
	organizationId: org?.organization_id,
	legalName: org?.company_legal_name,
	address: org?.address,
	city: org?.city,
	state: org?.state,
	zip: org?.zip,
	phone: org?.phone,
	aeName: org?.sales_rep_name,
	aeUserName: org?.sales_rep_id,
});

const withPrefix = (prefix, org) => {
	const o = pickOrganization(org);
	return {
		[`${prefix}Name`]: o.name,
		[`${prefix}Id`]: o.id,
		[`${prefix}OrganizationId`]: o.organizationId,
		[`${prefix}LegalName`]: o.legalName,
		[`${prefix}Address`]: o.address,
		[`${prefix}City`]: o.city,
		[`${prefix}State`]: o.state,
		[`${prefix}Zip`]: o.zip,
		[`${prefix}Phone`]: o.phone,
		[`${prefix}AeName`]: o.aeName,
		[`${prefix}AeUserName`]: o.aeUserName,
	};
};

/**
 * `tpo` object for updateLoan.
 * @param {object} args
 * @param {object} args.loanOriginator  broker record of the selected Loan Originator
 * @param {object} args.loanProcessor   broker record of the selected Loan Processor
 * @param {object} args.organizationDetails       getOrganizationDetails for the user's org
 * @param {object} args.childOrganizationDetails  getOrganizationDetails for the child org
 * @param {{name?: string, phone?: string, email?: string}} args.additionalContact
 * @param {boolean} args.isNdcOrCorr     loan channel is NDC or CORR
 * @param {boolean} args.useOrgCompany   exec/manager, TPO Secondary or child-org user
 * @param {boolean} args.includeBranch   branch fields apply (child organization)
 */
export const buildTpoLoanData = ({
	loanOriginator,
	loanProcessor,
	organizationDetails,
	childOrganizationDetails,
	additionalContact,
	isNdcOrCorr,
	useOrgCompany,
	includeBranch,
}) => {
	const tpo = {
		loAeName: loanOriginator?.organization?.sales_rep_id,
		...withPrefix("company", useOrgCompany ? organizationDetails : loanOriginator?.organization),
	};

	if (includeBranch) Object.assign(tpo, withPrefix("branch", childOrganizationDetails));

	if (isNdcOrCorr) {
		tpo.loEmail = additionalContact?.email;
		tpo.loName = additionalContact?.name;
		tpo.loBusinessPhone = additionalContact?.phone;
	} else {
		tpo.loEmail = loanOriginator?.user?.email;
		tpo.loStatus = loanOriginator?.login_enabled;
		tpo.loAddress = loanOriginator?.address;
		tpo.loCity = loanOriginator?.city;
		tpo.loState = loanOriginator?.state;
		tpo.loZip = loanOriginator?.zip_code;
		tpo.loName = buildLoLpFullName(loanOriginator);
		tpo.loBusinessPhone = loanOriginator?.phone;
	}

	tpo.lpEmail = loanProcessor?.user?.email;
	tpo.lpStatus = loanProcessor?.login_enabled;
	tpo.lpAddress = loanProcessor?.address;
	tpo.lpCity = loanProcessor?.city;
	tpo.lpState = loanProcessor?.state;
	tpo.lpZip = loanProcessor?.zip_code;
	tpo.lpName = buildLoLpFullName(loanProcessor);
	tpo.lpBusinessPhone = loanProcessor?.phone;

	return tpo;
};

/** Loan-level originator (interviewer) fields sent alongside `tpo`. */
export const buildOriginatorInfoData = (loanOriginator) => ({
	originatorAddressLineText: loanOriginator?.organization?.address,
	interviewerEmail: loanOriginator?.user?.email,
	interviewerName: buildLoLpFullName(loanOriginator),
	originatorFirstName: loanOriginator?.user?.first_name,
	originatorLastName: loanOriginator?.user?.last_name,
	nmlsLoanOriginatorId: loanOriginator?.nmls_id,
	interviewerPhoneNumber: loanOriginator?.phone,
	interviewerLicenseIdentifier: "",
});

const isSet = (v) => v !== null && v !== undefined && v !== "";

/** Org fields 315/313/321/323/3237/1256 (skipped when the LO record has no value, instead of
 * sending an entry with no value), plus TPO.X62/TPO.X75 only when set, as strings. */
export const buildLoanContactFieldValues = ({ loanOriginator, loanOriginatorId, loanProcessorId }) => {
	const org = loanOriginator?.organization;
	const values = [
		{ id: 315, value: org?.name },
		{ id: 313, value: org?.city },
		{ id: 321, value: org?.state },
		{ id: 323, value: org?.zip },
		{ id: 3237, value: org?.nmls_id },
		{ id: 1256, value: org?.sales_rep_name },
	].filter((entry) => entry.value !== undefined && entry.value !== null);
	if (isSet(loanOriginatorId)) values.push({ id: "TPO.X62", value: String(loanOriginatorId) });
	if (isSet(loanProcessorId)) values.push({ id: "TPO.X75", value: String(loanProcessorId) });
	return values;
};

/** The wizard blocks loan creation unless the AE lookup returns at least one email. */
export const hasAeEmail = (aeResult) =>
	!!(aeResult?.ae_email || aeResult?.branch_ae_email || aeResult?.company_ae_email);

/** CX.LC.AE.EMAIL* fields written right after createLoan. */
export const buildAeEmailFieldValues = (aeResult, includeBranch) => {
	const values = [
		{
			id: "CX.LC.AE.EMAIL",
			value:
				aeResult?.loan_processor_ae_email ||
				aeResult?.branch_ae_email ||
				aeResult?.company_ae_email ||
				"",
		},
		{ id: "CX.LC.AE.EMAIL.COMPANY", value: aeResult?.company_ae_email || "" },
		{ id: "CX.LC.AE.EMAIL.LO", value: aeResult?.loan_processor_ae_email || "" },
	];
	if (includeBranch) {
		values.push({ id: "CX.LC.AE.EMAIL.BRANCH", value: aeResult?.branch_ae_email || "" });
	}
	return values;
};
